import type { V2Bag, V2Value } from "@/features/applications/api/applications.api";
import type { FieldDef, RuleName } from "./model";
import {
  formatPhone, formatSsn, formatZip, formatRouting,
  isSsn9, isEin, isItin, isPhone10, isZip, isRouting9, isEmail, isMcc4, isLettersOnly,
} from "@/utils/format";

interface Rule {
  mask?: (v: string) => string;
  validate: (value: string, bag: V2Bag) => string | null;
}

const RULES: Record<RuleName, Rule> = {
  ssn: {
    mask: formatSsn,
    validate: (v) => (isSsn9(v) ? null : "Enter a 9-digit Social Security Number."),
  },
  taxId: {
    validate: (v, bag) => {
      const type = typeof bag.taxIdType === "string" ? bag.taxIdType : "";
      if (type === "SSN") return isSsn9(v) ? null : "SSN must be 9 digits.";
      if (type === "ITIN") return isItin(v) ? null : "ITIN must be 9 digits starting with 9.";
      return isEin(v) ? null : "EIN must be 9 digits.";
    },
  },
  routing: {
    mask: formatRouting,
    validate: (v) => (isRouting9(v) ? null : "Routing number must be 9 digits."),
  },
  phone: {
    mask: formatPhone,
    validate: (v) => (isPhone10(v) ? null : "Enter a 10-digit phone number."),
  },
  zip: {
    mask: formatZip,
    validate: (v) => (isZip(v) ? null : "ZIP must be 5 or 9 digits."),
  },
  email: {
    validate: (v) => (isEmail(v) ? null : "Enter a valid email address."),
  },
  mcc: {
    validate: (v) => (isMcc4(v) ? null : "MCC/SIC must be a 4-digit code."),
  },
  lettersOnly: {
    validate: (v) => (isLettersOnly(v) ? null : "Letters only."),
  },
  accountMatch: {
    mask: (v) => v.replace(/\D/g, "").slice(0, 17),
    validate: (v, bag) => {
      const primary = typeof bag.accountNumber === "string" ? bag.accountNumber : "";
      return v === primary ? null : "Account numbers don't match.";
    },
  },
  accountNumber: {
    mask: (v) => v.replace(/\D/g, "").slice(0, 17),
    validate: (v) => {
      const d = v.replace(/\D/g, "");
      return d.length >= 1 && d.length <= 17 ? null : "Account number must be 1–17 digits.";
    },
  },
};

export function maskFor(rule: RuleName | undefined): ((v: string) => string) | undefined {
  return rule ? RULES[rule].mask : undefined;
}

export function validateRule(rule: RuleName | undefined, value: V2Value, bag: V2Bag): string | null {
  if (!rule) return null;
  if (value == null || value === "") return null;
  const s = typeof value === "string" ? value : String(value);
  return RULES[rule].validate(s, bag);
}

export function validateField(def: FieldDef, value: V2Value, bag: V2Bag): string | null {
  return validateRule(def.rule, value, bag) ?? def.check?.(value, bag) ?? null;
}
